import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Sparkle, ArrowUpRight, Microphone, MapPin, Translate, Users } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import EmptyState from "@/components/EmptyState";

export default function SpeakersPage() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expertise, setExpertise] = useState("");

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const params = {};
      if (q) params.q = q;
      if (expertise) params.expertise = expertise;
      api.get("/speakers", { params }).then((r) => setList(r.data)).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [q, expertise]);

  // Derive expertise pills from the loaded list
  const expertisePills = useMemo(() => {
    const set = new Map();
    list.forEach((s) => (s.expertise || []).forEach((x) => set.set(x, (set.get(x) || 0) + 1)));
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name]) => name);
  }, [list]);

  return (
    <div data-testid="speakers-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="overline mb-3">{t("speakersPage.breadcrumb")}</div>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-none mb-3">{t("speakersPage.title")}</h1>
          <p className="text-zinc-600 max-w-2xl">{t("speakersPage.intro")}</p>
        </div>
        <Link to="/speakers/me" data-testid="become-speaker-cta" className="btn-accent mt-2">
          <Microphone size={14} weight="fill" /> {t("speakersPage.becomeSpeakerCta")} <ArrowUpRight size={14} data-arrow="forward" />
        </Link>
      </div>

      <div className="mt-12 border-t border-zinc-950 pt-6 flex items-center gap-2 border-b border-zinc-300 focus-within:border-zinc-950">
        <MagnifyingGlass size={16} className="text-zinc-400" />
        <input data-testid="speaker-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("speakersPage.searchPlaceholder")} className="flex-1 bg-transparent py-2 outline-none text-sm" />
      </div>

      {expertisePills.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="expertise-pills">
          <button
            type="button"
            onClick={() => setExpertise("")}
            data-testid="expertise-all"
            className={`text-xs font-mono uppercase tracking-wider px-3 py-1 border ${!expertise ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-500 hover:border-zinc-950"}`}
          >
            {t("common.all")}
          </button>
          {expertisePills.map((x) => (
            <button
              key={x}
              type="button"
              onClick={() => setExpertise(expertise === x ? "" : x)}
              data-testid={`expertise-pill-${x}`}
              className={`text-xs font-mono uppercase tracking-wider px-3 py-1 border ${expertise === x ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-500 hover:border-zinc-950"}`}
            >
              {x}
            </button>
          ))}
        </div>
      )}

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
        {loading && list.length === 0 && (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} data-testid={`speaker-skeleton-${i}`} className="animate-pulse">
              <div className="aspect-[3/4] bg-zinc-100 border border-zinc-200" />
              <div className="mt-4 space-y-2">
                <div className="h-4 bg-zinc-100 w-3/4" />
                <div className="h-3 bg-zinc-100 w-1/2" />
                <div className="h-3 bg-zinc-100 w-2/3" />
              </div>
            </div>
          ))
        )}
        {list.map((s, idx) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx * 0.03, 0.4) }}>
            <Link to={`/speakers/${s.id}`} data-testid={`speaker-card-${idx}`} className="block group relative">
              {s.is_featured && (
                <div data-testid={`speaker-featured-${idx}`} className="absolute top-2 left-2 z-10 bg-blue-700 text-white text-[9px] font-mono uppercase tracking-[0.15em] px-2 py-1 flex items-center gap-1">
                  <Sparkle size={9} weight="fill" /> {t("speakersPage.featured")}
                </div>
              )}
              <div className="aspect-[3/4] bg-zinc-100 border border-zinc-200 overflow-hidden">
                {s.image_url ? (
                  <img src={s.image_url} alt={s.name} className="img-mono w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-7xl font-black text-zinc-300 bg-grid">{s.name?.[0]}</div>
                )}
              </div>
              <div className="mt-4 flex items-start gap-3">
                <div className="font-mono text-[10px] text-zinc-400 pt-1 tracking-[0.15em]">№ {String(idx + 1).padStart(3, "0")}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-lg leading-tight group-hover:text-blue-700 transition-colors">{s.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{s.title}</div>
                  <div className="text-[11px] text-zinc-400 mt-1 font-mono uppercase tracking-wider truncate">{s.organization}</div>
                  {(s.expertise || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(s.expertise || []).slice(0, 2).map((x) => (
                        <span key={x} className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 border border-zinc-200 px-1.5 py-0.5">{x}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
      {!loading && list.length === 0 && (
        <div className="mt-10">
          <EmptyState
            testid="speakers-empty"
            icon={<Users size={22} />}
            title={t("speakersPage.empty")}
            body={t("speakersPage.emptyBody")}
            action={{ label: t("speakersPage.becomeSpeakerCta"), to: "/speakers/me" }}
          />
        </div>
      )}

      {/* "List yourself" footer banner */}
      <section className="mt-24 border-t border-zinc-950 pt-10">
        <div className="grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-2 text-blue-700 flex items-center gap-1.5"><Sparkle size={11} weight="fill" /> {t("speakersPage.bannerEyebrow")}</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-[-0.03em] leading-[.95]">{t("speakersPage.bannerTitle")}</h2>
            <p className="mt-3 text-zinc-600 max-w-xl">{t("speakersPage.bannerBody")}</p>
          </div>
          <div className="md:col-span-4 md:text-end">
            <Link to="/speakers/me" data-testid="banner-become-cta" className="btn-primary">
              {t("speakersPage.bannerCta")} <ArrowUpRight size={14} data-arrow="forward" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
